import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import JobCard from '../components/jobs/JobCard';
import CreateJobModal from '../components/jobs/CreateJobModal';
import ApplyJobModal from '../components/jobs/ApplyJobModal';
import Button from '../components/common/Button';
import StateSearch from '../components/common/StateSearch';
import { EmailSubscriptionForm } from '../components/subscription/EmailSubscriptionForm';
import { useJobs } from '../hooks/useJobs';
import { deleteJob as deleteFirebaseJob } from '../lib/firebase/jobs';
import { deleteJob as deleteSupabaseJob } from '../lib/supabase/jobs';
import { getJobApplicationCounts } from '../lib/supabase/applications';
import { useScrollPosition } from '../hooks/useScrollPosition';
import type { Job } from '../types/jobs';
import type { USState } from '../utils/states';

const JOB_TITLES = [
  'Ranch Hand',
  'Foreman',
  'Cow Boss',
  'Manager',
  'Property Manager',
  'Colt Starter',
  'Guide',
  'Cook',
  'Admin'
] as const;

export default function Jobs() {
  const { user } = useAuth();
  const { jobs, loading, error, refreshJobs } = useJobs();
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedState, setSelectedState] = useState<USState | null>(() => {
    const saved = sessionStorage.getItem('jobsSelectedState');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>(() => {
    return sessionStorage.getItem('jobsSelectedTitle') || '';
  });
  const [applicationCounts, setApplicationCounts] = useState<Record<string, number>>({});
  const { restoreScrollPosition, clearScrollPosition } = useScrollPosition({
    key: 'jobsListScroll',
    storage: 'session'
  });

  useEffect(() => {
    setFilteredJobs(jobs);

    if (!loading) {
      restoreScrollPosition();
    }
  }, [jobs, loading, restoreScrollPosition]);

  useEffect(() => {
    const loadApplicationCounts = async () => {
      if (jobs.length > 0) {
        const jobIds = jobs.map(job => job.id);
        const counts = await getJobApplicationCounts(jobIds);
        setApplicationCounts(counts);
      }
    };

    loadApplicationCounts();
  }, [jobs]);

  useEffect(() => {
    sessionStorage.setItem('jobsSelectedState', JSON.stringify(selectedState));
    sessionStorage.setItem('jobsSelectedTitle', selectedJobTitle);
  }, [selectedState, selectedJobTitle]);

  useEffect(() => {
    let filtered = jobs;

    if (selectedState) {
      filtered = filtered.filter(job =>
        job.location.toLowerCase().includes(selectedState.name.toLowerCase()) ||
        job.location.toLowerCase().includes(selectedState.abbreviation.toLowerCase())
      );
    }

    if (selectedJobTitle) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(selectedJobTitle.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  }, [selectedState, selectedJobTitle, jobs]);

  const clearFilters = () => {
    setSelectedState(null);
    setSelectedJobTitle('');
    sessionStorage.removeItem('jobsSelectedState');
    sessionStorage.removeItem('jobsSelectedTitle');
  };

  const handleDelete = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      // Try deleting from both Firebase and Supabase
      const [firebaseResult, supabaseResult] = await Promise.all([
        deleteFirebaseJob(jobId),
        deleteSupabaseJob(jobId)
      ]);
      
      // If both fail, show error
      if (firebaseResult.error && supabaseResult.error) {
        throw new Error('Failed to delete job from both systems');
      }
      
      // Refresh the jobs list
      refreshJobs();
    } catch (err) {
      console.error('Error deleting job:', err);
      alert((err as Error).message);
    }
  };

  const handleApply = (jobId: string) => {
    if (!user) {
      document.getElementById('auth-modal')?.click();
      return;
    }
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
    }
  };

  const handleToggleComplete = (jobId: string, isCompleted: boolean) => {
    setFilteredJobs(prev =>
      prev.map(job =>
        job.id === jobId ? { ...job, isCompleted } : job
      )
    );
  };

  return (
    <>
      <Helmet>
        <title>Find Ranch Jobs | RanchWorkUSA</title>
        <meta
          name="description"
          content="Looking for ranch work opportunities? RanchWorkUSA has listings for full-time, part-time, and seasonal ranch positions across the country. Find your next ranch job here!"
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="relative mb-8 rounded-xl overflow-hidden shadow-xl">
          <img
            src="/image copy copy copy copy copy copy copy copy copy copy copy copy.png"
            alt="Majestic mountain landscape with snowy peaks and forest"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8">
              <h1 className="text-4xl font-bold text-white mb-2">Find Ranch Jobs</h1>
              <p className="text-xl text-white/90">
                Browse open ranch and agriculture positions
              </p>
            </div>
          </div>
          {user && (
            <div className="absolute top-4 right-4">
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => setShowCreateModal(true)}
              >
                Post Job
              </Button>
            </div>
          )}
        </div>

        <div className="mb-6 max-w-4xl mx-auto">
          <EmailSubscriptionForm compact />
        </div>

        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto">
            <div className="flex-1">
              <StateSearch
                onStateSelect={setSelectedState}
                selectedState={selectedState}
              />
            </div>
            <div className="flex-1">
              <select
                value={selectedJobTitle}
                onChange={(e) => setSelectedJobTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Job Titles</option>
                {JOB_TITLES.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>
          </div>

          {(selectedState || selectedJobTitle) && (
            <div className="flex flex-wrap items-center gap-3 max-w-4xl mx-auto">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedState && (
                <button
                  onClick={() => setSelectedState(null)}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200"
                >
                  {selectedState.name}
                  <X className="h-3 w-3" />
                </button>
              )}
              {selectedJobTitle && (
                <button
                  onClick={() => setSelectedJobTitle('')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200"
                >
                  {selectedJobTitle}
                  <X className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-800">
            <p>{error.message}</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">
              {selectedState || selectedJobTitle
                ? 'No jobs match your search criteria. Try adjusting your filters.'
                : 'No jobs available at the moment. Check back soon for new opportunities!'}
            </p>
            {(selectedState || selectedJobTitle) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onApply={handleApply}
                onDelete={handleDelete}
                canDelete={user?.id === job.farmerId}
                applicationCount={applicationCounts[job.id] || 0}
                onToggleComplete={handleToggleComplete}
              />
            ))}
          </div>
        )}

        {selectedJob && (
          <ApplyJobModal
            job={selectedJob}
            onClose={() => setSelectedJob(null)}
          />
        )}

        {showCreateModal && (
          <CreateJobModal onClose={() => {
            setShowCreateModal(false);
            refreshJobs();
          }} />
        )}
      </div>
    </>
  );
}